import { useCallback, useMemo } from "react";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { SelectOption } from "@/lib/molecules/select";

/**
 * Hook that keeps selectedGroup and selectedApartment in sync.
 * - When group changes: if current apartment doesn't belong to the new group, resets apartment to "All" (0).
 * - When apartment changes: updates group to match the apartment's groupId.
 * - Returns filtered apartment options based on the selected group.
 */
export const useGroupApartmentSync = () => {
    const { selectedGroup, selectedApartment, groupOptions } = useAppData();
    const apartmentEntities = useActiveApartmentEntities();
    const apartmentOptions = useActiveApartmentOptions();
    const { setSelectedGroup, setSelectedApartment } = useUserSettingsDispatch();

    const filteredApartmentOptions: SelectOption[] = useMemo(() => {
        if (selectedGroup === 0) return apartmentOptions;
        const filtered = apartmentOptions.filter(
            (a) => a.value === "0" || apartmentEntities[Number(a.value)]?.groupId === selectedGroup,
        );
        return filtered;
    }, [selectedGroup, apartmentOptions, apartmentEntities]);

    const handleGroupChange = useCallback(
        (groupId: number) => {
            setSelectedGroup(groupId);
            if (groupId !== 0 && selectedApartment !== 0) {
                const apt = apartmentEntities[selectedApartment];
                if (apt && apt.groupId !== groupId) {
                    setSelectedApartment(0);
                }
            }
        },
        [selectedApartment, apartmentEntities, setSelectedGroup, setSelectedApartment],
    );

    const handleApartmentChange = useCallback(
        (apartmentId: number) => {
            setSelectedApartment(apartmentId);
            if (apartmentId !== 0) {
                const apt = apartmentEntities[apartmentId];
                if (apt && apt.groupId !== selectedGroup) {
                    setSelectedGroup(apt.groupId);
                }
            }
        },
        [selectedGroup, apartmentEntities, setSelectedGroup, setSelectedApartment],
    );

    return {
        selectedGroup,
        selectedApartment,
        groupOptions,
        apartmentOptions: filteredApartmentOptions,
        handleGroupChange,
        handleApartmentChange,
    };
};
